import { Inject, Injectable } from '@angular/core';
import {
  MeetingSource,
  MeetingSourceOrigin,
  MeetingSourceQuery,
  MeetingSourceStatus,
  MeetingsService,
} from '@vendasta/meetings';
import { Observable, ReplaySubject, combineLatest, concat, timer } from 'rxjs';
import { debounceTime, first, map, shareReplay, switchMap } from 'rxjs/operators';
import { MeetingIntegrationsServiceModule } from './meeting-integrations-service.module';
import {
  NAMESPACE_TOKEN,
  ORIGIN_TOKEN,
  RedirectInfo,
  SOURCE_REDIRECT_TOKEN,
  USER_ID_TOKEN,
} from './meeting-integrations.data.module';

export type SourcesState = {
  [source in MeetingSource]: {
    status: MeetingSourceStatus;
    connectDisconnectUrls: {
      [key: string]: string;
    };
  };
};

@Injectable({
  providedIn: MeetingIntegrationsServiceModule,
})
export class MeetingIntegrationsStoreService {
  private readonly signalToCallLoadSources$$: ReplaySubject<void> = new ReplaySubject();
  private readonly loadSourceResponse$: Observable<SourcesState>;

  constructor(
    @Inject(USER_ID_TOKEN) private readonly userId$: Observable<string>,
    @Inject(NAMESPACE_TOKEN) private readonly namespace$: Observable<string>,
    @Inject(ORIGIN_TOKEN) private readonly origin: MeetingSourceOrigin,
    @Inject(SOURCE_REDIRECT_TOKEN) private readonly sourceRedirectInfo$: Observable<RedirectInfo>,
    private readonly meetingsService: MeetingsService,
  ) {
    this.loadSourceResponse$ = this.signalToCallLoadSources$$.pipe(
      // Debounce by 1 second to avoid excessive number of calls
      debounceTime(1000),
      switchMap(() => {
        // The backend updates the connection asynchronously - so there may be a race condition on page load.
        // Try and get around that by performing more calls over a preiod of time.
        // Yes, this is very hacky.
        return concat(
          this.loadSourcesCall(),
          timer(1500).pipe(
            first(),
            switchMap(() => this.loadSourcesCall()),
          ),
          timer(3000).pipe(
            first(),
            switchMap(() => this.loadSourcesCall()),
          ),
          timer(5000).pipe(
            first(),
            switchMap(() => this.loadSourcesCall()),
          ),
        );
      }),
      shareReplay(1),
    );
  }

  loadSources(): Observable<SourcesState> {
    this.signalToCallLoadSources$$.next();
    return this.loadSourceResponse$;
  }

  private loadSourcesCall(): Observable<SourcesState> {
    const keysWithQueries$ = this.sourceRedirectInfo$.pipe(
      map((redirectInfo) => {
        return [
          MeetingSource.MEETING_SOURCE_GOOGLE_MEET,
          MeetingSource.MEETING_SOURCE_ZOOM,
          MeetingSource.MEETING_SOURCE_MICROSOFT,
        ]
          .map((source) => {
            return Object.keys(redirectInfo).map((key) => {
              return {
                query: {
                  source,
                  connectNextUrl: redirectInfo[key].connectionSuccessfulReturnURL,
                  disconnectNextUrl: redirectInfo[key].disconnectSuccessReturnURL,
                },
                key: key,
              };
            });
          })
          .reduce((acc, curr) => [...acc, ...curr], []); // flatten array
      }),
    );

    return combineLatest([keysWithQueries$, this.userId$, this.namespace$]).pipe(
      switchMap(([keyWithQuery, userId, namespace]) => {
        return this.meetingsService
          .listForUser(
            userId,
            namespace,
            this.origin,
            keyWithQuery.map((k) => new MeetingSourceQuery(k.query)),
          )
          .pipe(
            map((response) => {
              const sourceQueryResponses = response.sourceInfo;
              return sourceQueryResponses.reduce((responseMap, sourceQuery, currentIndex) => {
                return {
                  ...responseMap,
                  [sourceQuery.source]: {
                    ...responseMap[sourceQuery.source],
                    status: sourceQuery.status,
                    connectDisconnectUrls: {
                      ...((responseMap[sourceQuery.source] || {}).connectDisconnectUrls || {}),
                      [keyWithQuery[currentIndex].key]: sourceQuery.connectDisconnectUrl,
                    },
                  },
                };
              }, {} as SourcesState);
            }),
          );
      }),
      shareReplay(1),
    );
  }
}
